'use client';

/**
 * The `Address` field's editor (LF-5988 / LEADS-21, `functional-spec.md` §4.4).
 *
 * **A popover rather than an input in the slot, because an address is five parts,
 * not one.** §4.3's rule is that editor weight scales with the consequence of the
 * edit; this is the shape-of-the-value corollary. `lead.address` is a
 * `PostalAddress` — the same object as `contacts.address`, so the two can be copied
 * across — and flattening it into one line for editing would mean parsing it back,
 * which silently loses whatever the parser guesses wrong.
 *
 * It keeps §4.3's contract intact: the draft is live and untrimmed, `Escape` reverts
 * to the address the field opened with, and dismissing commits. `Tab` moves between
 * the five parts natively — the grid's field-to-field chaining stops at this row
 * deliberately, since tabbing *out* of a half-typed address to the next card is the
 * opposite of helpful.
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { AddressSearch } from '@/components/shared/address-search';
import { formatPostalAddress } from '@/lib/services/matters/utils';
import { cn } from '@/lib/utils';
import type { PostalAddress } from '@/lib/services/matters/types';
import {
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_POPOVER_COLLISION_PADDING,
  ATTRIBUTE_POPOVER_PANEL,
  ATTRIBUTE_POPOVER_PANEL_WIDE,
  ATTRIBUTE_COMPOSITE_PART,
  ATTRIBUTE_COMPOSITE_PART_CONTROL,
  ATTRIBUTE_COMPOSITE_PART_LABEL,
  ATTRIBUTE_COMPOSITE_PARTS,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import { attributePlaceholder } from './attribute-placeholder';
import { toAddressDraft, toStructuredAddress } from './attribute-address-draft';
import type { AttributeDraft } from './attribute-grid';
import type { AttributeOption } from './attribute-types';
import { useAttributePopoverCommit } from './use-attribute-popover-commit';

/**
 * The five parts, in the order they are written on an envelope.
 *
 * Derived from `PostalAddress`'s own keys via `satisfies`, so a part added to the
 * shared type cannot be silently missing from this editor — the compiler names it.
 */
const ADDRESS_PARTS = [
  { key: 'streetAddress', label: 'Street address' },
  { key: 'suburb', label: 'Suburb' },
  { key: 'state', label: 'State' },
  { key: 'postcode', label: 'Postcode' },
  { key: 'country', label: 'Country' }
] as const satisfies readonly { key: keyof PostalAddress; label: string }[];

/**
 * An address with every part blank is **cleared**, not stored as five empty
 * strings — otherwise `firmHoldsWill`-style "is anything captured here?" reads
 * would see an object and answer yes.
 */
function normalise(address: PostalAddress): PostalAddress | null {
  const parts = ADDRESS_PARTS.map(({ key }) => address[key] ?? '');
  return parts.some((part) => part.trim().length > 0) ? address : null;
}

export function AttributeAddressEditor({
  label,
  address,
  unified = true,
  stateOptions,
  onDraft,
  onRevert,
  onCommit
}: {
  label: string;
  address: PostalAddress | null;
  /**
   * **The redesigned popover** (LF-6359 / [ATTR-9] §3) — a lookup at the top and
   * the manual parts two to a line, rather than five bare inputs stacked.
   *
   * The five stacked inputs below were what an enquiry's address had always
   * been: typed off a phone call, with no lookup in front of them. A contact's
   * is the one the letter goes to, and the production contact form has always
   * had the search — so the unified arm renders the real {@link AddressSearch},
   * which is both halves at once (it searches, and it reveals the structured
   * fields when the lookup cannot find the place, which for a rural or
   * new-estate address is routine rather than an edge case).
   *
   * **Defaults to `true`** (LF-6388 / [ATTR-10]): an address is an address, and
   * the enquiries drawer gets the lookup for the same reason Contacts did. The
   * stacked-inputs arm survives only for the one call site still passing
   * `AttributeGrid`'s `legacyChrome`.
   */
  unified?: boolean;
  /** Passed to `AddressSearch` (VALE-1940): see the `address` editor kind. */
  stateOptions?: readonly AttributeOption[];
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
}) {
  const set = (key: keyof PostalAddress, value: string) => {
    // Untrimmed, per §4.3 — trimming here strips the space between "12 Bourke"
    // and "Street" the moment it is typed.
    onDraft(normalise({ ...(address ?? {}), [key]: value }));
  };

  /*
    `Enter` commits and closes, `Escape` reverts, dismissing commits: the one
    contract all seven composite popover editors share (VALE-1321). See
    `./use-attribute-popover-commit`, which owns every subtlety in it.
  */
  const popoverCommit = useAttributePopoverCommit({
    draft: address,
    onRevert,
    onCommit
  });

  return (
    <Popover {...popoverCommit.popover}>
      <PopoverTrigger asChild>
        {/*
          Left-aligned and cell-width, matching the read-out it replaces
          (LF-6099): the trigger sits in the same slot, so the address must not
          jump sideways as the popover opens. `bg-slate-100` is the same
          highlight the value cell takes on hover, held on while the editor is
          open — the field being edited stays visible behind the popover.
        */}
        {/*
          `min-w-0 flex-1` on the unified path rather than `w-full`: the cell
          now also holds the copy and clear buttons while the popover is open
          (LF-6359 §1), and `w-full` would claim the whole cell and shove them
          out of it. Flexing lets the trigger give way exactly as much as the
          icons need, so the address text is never underneath them.

          Bare, too — the cell draws the `slate-50` fill and the ring, so the
          trigger's own `bg-slate-100` would be a third grey agreeing with
          nothing.
        */}
        <button
          type="button"
          aria-label={label}
          className={cn(
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_INSET,
            ATTRIBUTE_LEADING,
            'text-foreground truncate rounded-md text-left',
            unified
              ? cn('min-w-0 flex-1', ATTRIBUTE_EDITOR_BARE)
              : 'bg-muted w-full'
          )}
        >
          {formatPostalAddress(address ?? undefined) ||
            attributePlaceholder(label)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={ATTRIBUTE_POPOVER_COLLISION_PADDING}
        /*
          Both widths come from {@link ATTRIBUTE_POPOVER_PANEL} and its wide
          variant (VALE-863) — the panel the `name` editor shares, so the two
          composite editors on this treatment stop being two panels that merely
          resemble each other. Why the `unified` arm is the wide one, and the
          viewport cap it carries, are recorded on the token.
        */
        /*
          {@link ATTRIBUTE_COMPOSITE_PARTS} on BOTH arms since VALE-1710. The
          unified arm delegates its whole column to `AddressSearch` and so was a
          plain block — invisible while it had one child, and not once the
          confirm row is a second. The gap above that control has to be the same
          6.8px it is on every other composite panel, or the address popover
          reads looser than the ones it sits beside all over again.
        */
        className={cn(
          unified ? ATTRIBUTE_POPOVER_PANEL_WIDE : ATTRIBUTE_POPOVER_PANEL,
          ATTRIBUTE_COMPOSITE_PARTS
        )}
        {...popoverCommit.content}
      >
        {unified ? (
          /*
            `toStructuredAddress` / `toAddressDraft` are the one named boundary
            between the grid's `PostalAddress` draft and `AddressSearch`'s
            `StructuredAddress` — see `./attribute-address-draft`. Written out here
            it would be five `?? ''`s that the next adopter re-derives and one of
            them gets wrong, and a dropped part in an address is a letter that
            does not arrive.
          */
          <AddressSearch
            value={toStructuredAddress(address)}
            fieldLayout="pairs"
            stateOptions={stateOptions}
            onChange={(next) => onDraft(toAddressDraft(next))}
            /*
              The five parts are rendered by `AddressSearch`, so the popover's
              geometry has to reach them — otherwise the panel is compact and
              its contents are still page-form height, which is the state Tom's
              review was looking at. `partChrome` is the seam; the tokens stay
              owned here, one layer above it.
            */
            partChrome={{
              parts: ATTRIBUTE_COMPOSITE_PARTS,
              part: ATTRIBUTE_COMPOSITE_PART,
              label: ATTRIBUTE_COMPOSITE_PART_LABEL,
              control: ATTRIBUTE_COMPOSITE_PART_CONTROL,
              // The list a reader scans while typing, on the panel's own value
              // size rather than the page form's.
              suggestion: ATTRIBUTE_VALUE_TEXT,
              hint: ATTRIBUTE_COMPOSITE_PART_LABEL
            }}
          />
        ) : (
          ADDRESS_PARTS.map(({ key, label: partLabel }, index) => (
            <div key={key} className={ATTRIBUTE_COMPOSITE_PART}>
              <Label
                htmlFor={`lead-address-${key}`}
                className={ATTRIBUTE_COMPOSITE_PART_LABEL}
              >
                {partLabel}
              </Label>
              <Input
                id={`lead-address-${key}`}
                autoFocus={index === 0}
                value={address?.[key] ?? ''}
                onChange={(event) => set(key, event.target.value)}
                className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
              />
            </div>
          ))
        )}
        {popoverCommit.confirm}
      </PopoverContent>
    </Popover>
  );
}
